/*
* Palo Alto Theme
*
* Use this file to add custom Javascript to Palo Alto.  Keeping your custom
* Javascript in this fill will make it easier to update Palo Alto. In order
* to use this file you will need to open layout/theme.liquid and uncomment
* the custom.js script import line near the bottom of the file.
*/


(function() {
  // Add custom code below this line

  const MEDIA_HOVER_VISIBLE_CLASS = 'product__media__hover-img--visible';

  function fetchProduct(handle) {
    return fetch(`${window.theme.routes.root}products/${handle}.js`)
      .then(function(response) {
        return response.json();
      })
      .catch(function() {
        return null;
      });
  }

  /**
   * Constrain a Shopify CDN image URL to a max width.
   * Shopify supports appending ?width=N or inserting _{W}x before the extension.
   * We use the query-param approach which works for all CDN image URLs.
   * @param {string} url  - Raw Shopify CDN image URL
   * @param {number} width - Desired max width in px
   * @returns {string}
   */
  function constrainImageUrl(url, width) {
    if (!url || !width) return url;
    try {
      const u = new URL(url);
      u.searchParams.set('width', width);
      return u.toString();
    } catch (_) {
      // fallback: append as query string if URL is relative/malformed
      const separator = url.includes('?') ? '&' : '?';
      return url + separator + 'width=' + width;
    }
  }

  class ProductGridItemLinked extends HTMLElement {
    constructor() {
      super();
      this.productDataCache = {};
      this.hoverMediaObserver = null;
      this.initialized = false;
    }

    connectedCallback() {
      requestAnimationFrame(() => this.initialize());
    }

    initialize() {
      if (this.initialized || !this.isConnected) return;
      this.initialized = true;

      this.swatches = this.querySelectorAll('.js-product-grid-item-linked-swatch');
      this.colorLabelText = this.querySelector('.js-product-grid-item-color-label-text');
      this.productItem = this.closest('.product-grid-item');
      if (!this.productItem) return;

      this.productPrice = this.productItem.querySelector('.product-grid-item__price');
      this.productTitle = this.productItem.querySelector('.product-grid-item__title');
      this.productMediaHolder = this.productItem.querySelector('.product__media__holder');
      this.productImage = this.productItem.querySelector('[data-product-image] img');
      this.productHoverImage = this.productItem.querySelector('[data-product-image-hover] img');
      this.quickViewBtn = this.productItem.querySelector('[data-button-quick-view]');
      this.swiperEl = this.productItem.querySelector('.js-product-grid-item-linked-swatches');
      this.moreButton = this.productItem.querySelector('.js-product-grid-item-linked-swatch-more');
      this.activeSwatch = this.querySelector('.js-product-grid-item-linked-swatch.selected') || null;
      this.moreButton?.addEventListener('click', () => this.toggleSwatches());
      this.addEventListener('mouseover', (event) => {
        const swatch = event.target.closest('.js-product-grid-item-linked-swatch');
        if (!swatch || !this.contains(swatch) || swatch === this.activeSwatch) return;
        this.activeSwatch = swatch;
        this.applySwatchState(swatch);
      });

      this.addEventListener('focusin', (event) => {
        const swatch = event.target.closest('.js-product-grid-item-linked-swatch');
        if (!swatch || !this.contains(swatch) || swatch === this.activeSwatch) return;
        this.activeSwatch = swatch;
        this.applySwatchState(swatch);
      });

      this.addEventListener('click', (event) => {
        const swatch = event.target.closest('.js-product-grid-item-linked-swatch');
        if (!swatch || !this.contains(swatch)) return;
        event.preventDefault();
        this.activeSwatch = swatch;
        this.applySwatchState(swatch);
      });

      if (this.productMediaHolder) {
        this.productMediaHolder.addEventListener('mouseenter', () => {
          this.syncActiveSwatchMedia();
          requestAnimationFrame(() => this.syncActiveSwatchMedia());
        });
      }
      this.observeHoverMediaChanges();
      this.applyActiveCollectionColor();
    }

    toggleSwatches() {
      if (!this.swiperEl) return;

      const isExpanded = this.swiperEl.classList.toggle('is-expanded');
      this.moreButton?.setAttribute('aria-expanded', String(isExpanded));
      this.moreButton?.setAttribute('aria-label', isExpanded ? 'Show fewer colors' : 'Show more colors');
    }

    applyActiveCollectionColor() {
      const collection = this.closest('[data-section-type="collection-template"]');
      if (!collection) return;

      const activeColors = Array.from(
        collection.querySelectorAll('[data-color-filter] .filter__input:checked')
      )
        .map((input) => this.normalizeColor(input.dataset.filterValueLabel))
        .filter(Boolean);

      if (!activeColors.length) {
        this.productItem.hidden = false;
        return;
      }

      const matchingSwatch = Array.from(this.swatches).find((swatch) => {
        return activeColors.includes(this.normalizeColor(swatch.dataset.colorLabel));
      });

      // Shopify's broad colour taxonomy can return Khaki/Dark Khaki for a
      // Brown filter.  Keep a card only when its linked colours contain the
      // exact selected label; otherwise it is not an exact Brown result.
      if (!matchingSwatch) {
        this.productItem.hidden = true;
        return;
      }

      // Make the exact matching colour both visible and selected, rather than
      // leaving the card on the linked product's default colour.
      this.productItem.hidden = false;
      if (this.swiperEl && matchingSwatch !== this.swiperEl.firstElementChild) {
        this.swiperEl.insertBefore(matchingSwatch, this.swiperEl.firstElementChild);
      }
      this.applySwatchState(matchingSwatch);
    }

    normalizeColor(value) {
      return (value || '').trim().replace(/\s+/g, ' ').toLowerCase();
    }

    applySwatchState(swatch) {
      if (!swatch) return;
      this.activeSwatch = swatch;
      const featuredImage = swatch.getAttribute('data-featured-image');
      const secondaryImage = swatch.getAttribute('data-secondary-image') || featuredImage;

      this.updateSelectedSwatch(swatch);
      this.updateColorLabelText(swatch.getAttribute('data-color-label'));
      this.updateProductPrice(swatch.getAttribute('data-price'));
      this.updateProductTitle(swatch.getAttribute('data-title'));
      this.updateProductLink(swatch.getAttribute('href'));
      this.updateProductImage(featuredImage);
      this.updateProductHoverImage(secondaryImage);
      this.updateQuickViewBtn(swatch.getAttribute('data-handle'));
      this.syncSwatchMediaFromHandle(swatch);
    }

    disconnectedCallback() {
      if (this.hoverMediaObserver) {
        this.hoverMediaObserver.disconnect();
        this.hoverMediaObserver = null;
      }
    }

    observeHoverMediaChanges() {
      const hoverContainer = this.productItem ? this.productItem.querySelector('.product__media__hover') : null;
      if (!hoverContainer || this.hoverMediaObserver) return;
      this.hoverMediaObserver = new MutationObserver(() => {
        this.syncActiveSwatchMedia();
      });
      this.hoverMediaObserver.observe(hoverContainer, { childList: true, subtree: true });
    }

    syncSwatchMediaFromHandle(swatch) {
      const handle = swatch.getAttribute('data-handle');
      if (!handle) return;

      if (this.productDataCache[handle]) {
        this.applyProductDataMedia(this.productDataCache[handle]);
        return;
      }
      fetchProduct(handle).then((productData) => {
        if (!productData || !this.activeSwatch || this.activeSwatch.getAttribute('data-handle') !== handle) return;
        this.productDataCache[handle] = productData;
        this.applyProductDataMedia(productData);
      });
    }

    applyProductDataMedia(productData) {
      const images = productData.images || [];
      const featuredImage = productData.featured_image || images[0] || '';
      const secondaryImage = images[1] || featuredImage;

      if (featuredImage) this.updateProductImage(constrainImageUrl(featuredImage, 800));
      if (secondaryImage) this.updateProductHoverImage(constrainImageUrl(secondaryImage, 800));
    }

    syncActiveSwatchMedia() {
      const swatch = this.activeSwatch || this.querySelector('.js-product-grid-item-linked-swatch.selected');
      if (!swatch) return;
      this.syncSwatchMediaFromHandle(swatch);
      const featuredImage = swatch.getAttribute('data-featured-image');
      const secondaryImage = swatch.getAttribute('data-secondary-image') || featuredImage;
      this.updateProductImage(featuredImage);
      this.updateProductHoverImage(secondaryImage);
    }

    updateSelectedSwatch(swatch) {
      this.swatches.forEach((s) => { s.classList.remove('selected'); });
      swatch.classList.add('selected');
    }

    updateColorLabelText(colorLabel) {
      if (!this.colorLabelText) return;
      this.colorLabelText.textContent = colorLabel;
    }

    updateProductPrice(price) {
      if (!this.productPrice) return;
      this.productPrice.textContent = price;
    }

    updateProductTitle(title) {
      if (!this.productTitle) return;
      this.productTitle.textContent = title;
    }

    updateProductLink(link) {
      if (this.productMediaHolder) this.productMediaHolder.setAttribute('href', link);
      if (this.productTitle) this.productTitle.setAttribute('href', link);
      if (this.productPrice) this.productPrice.setAttribute('href', link);
    }

    updateProductImage(image) {
      if (!this.productImage || !image) return;
      this.productImage.setAttribute('src', image);
      this.productImage.setAttribute('srcset', image);
      this.productImage.setAttribute('alt', this.productTitle ? this.productTitle.textContent : '');
    }

    updateProductHoverImage(image) {
      if (!image) return;
      const hoverMediaElements = this.productItem ? this.productItem.querySelectorAll('[data-product-image-hover]') : [];
      const hoverContainerImages = this.productItem ? this.productItem.querySelectorAll('.product__media__hover img') : [];

      if (hoverContainerImages.length) {
        hoverContainerImages.forEach((hoverImage) => {
          hoverImage.setAttribute('src', image);
          hoverImage.setAttribute('srcset', image);
          hoverImage.setAttribute('alt', this.productTitle ? this.productTitle.textContent : '');
        });
      }
      const hoverContainerSources = this.productItem ? this.productItem.querySelectorAll('.product__media__hover source') : [];
      if (hoverContainerSources.length) {
        hoverContainerSources.forEach((source) => { source.setAttribute('srcset', image); });
      }

      if (hoverMediaElements.length) {
        hoverMediaElements.forEach((hoverMedia) => {
          hoverMedia.classList.remove(MEDIA_HOVER_VISIBLE_CLASS);
          const hoverMediaImage = hoverMedia.tagName === 'IMG' ? hoverMedia : hoverMedia.querySelector('img');
          if (hoverMediaImage) {
            hoverMediaImage.setAttribute('src', image);
            hoverMediaImage.setAttribute('srcset', image);
            hoverMediaImage.setAttribute('alt', this.productTitle ? this.productTitle.textContent : '');
          }
        });
        const activeHoverMedia = hoverMediaElements[0];
        activeHoverMedia.classList.add(MEDIA_HOVER_VISIBLE_CLASS);
        return;
      }

      if (!this.productHoverImage) return;
      this.productHoverImage.setAttribute('src', image);
      this.productHoverImage.setAttribute('srcset', image);
      this.productHoverImage.setAttribute('alt', this.productTitle ? this.productTitle.textContent : '');
    }

    updateQuickViewBtn(handle) {
      if (!this.quickViewBtn) return;
      this.quickViewBtn.setAttribute('data-handle', handle);
    }
  }

  if (!customElements.get('product-grid-item-linked')) {
    customElements.define('product-grid-item-linked', ProductGridItemLinked);
  }

  // ============================================================
  // Vertical product thumbnails — desktop left-side layout
  //
  // custom.js is loaded as `defer` and executes AFTER vendor.js,
  // theme.js and product-info.js (all also defer, loaded earlier).
  // By the time this code runs, Flickity is already initialised on
  // every product-info element on the page, so we can act immediately
  // without waiting for DOMContentLoaded.
  // ============================================================
  (function () {
    var DESKTOP_MIN = 768;

    // Some mobile browsers expose a desktop-sized layout viewport.  A width
    // check alone can therefore apply the desktop thumbnail layout on a phone.
    // Require a mouse-style pointer and hover capability as well.
    function usesDesktopThumbnailLayout() {
      return window.matchMedia(
        '(min-width: ' + DESKTOP_MIN + 'px) and (hover: hover) and (pointer: fine)'
      ).matches;
    }

    function applyVertical(productInfo) {
      if (!usesDesktopThumbnailLayout()) return false;

      var thumbContainer = productInfo.querySelector('[data-product-single-media-thumbs]');
      var mainSlider     = productInfo.querySelector('[data-product-single-media-slider]');
      if (!thumbContainer || !mainSlider) return false;

      var F = window.theme && window.theme.Flickity;
      if (!F) return false;

      var navFlkty  = F.data(thumbContainer);
      var mainFlkty = F.data(mainSlider);
      if (!navFlkty || !mainFlkty) return false;

      // Guard: already handled this exact Flickity instance
      if (thumbContainer._vFlkty === navFlkty) return true;
      thumbContainer._vFlkty = navFlkty;

      // Locate the gallery wrapper
      var gallery = thumbContainer.closest('[data-product-single-media-group]');

      // Null out product-info's own flktyNav reference so its
      // resizeFlickityNav() handler won't crash after we destroy it
      var piEl = thumbContainer.closest('product-info');
      if (piEl) piEl.flktyNav = null;

      // Destroy nav Flickity FIRST, then add layout classes
      // (order matters: Flickity must be gone before flex layout applies)
      navFlkty.destroy();

      if (gallery) gallery.classList.add('gallery--v-thumbs');
      thumbContainer.classList.add('v-thumbs-ready');

      // Force main slider to recalculate its width in the new flex container
      requestAnimationFrame(function () {
        mainFlkty.resize();
        syncHeight();
      });

      // Keep thumbnail column height in sync with the main image
      function syncHeight() {
        var h = mainSlider.offsetHeight;
        if (gallery && h) {
          gallery.style.setProperty('--thumb-col-height', h + 'px');
        }
      }
      mainFlkty.on('resize', syncHeight);
      mainFlkty.on('settle', syncHeight);

      // Click: navigate the main slider
      // Both .product-single__thumbnail and slide wrappers carry
      // data-id="{{ media.id }}" (plain numeric) — safe to compare.
      var thumbs = Array.from(thumbContainer.querySelectorAll('.product-single__thumbnail'));
      thumbs.forEach(function (thumb) {
        if (thumb._vClickBound) return;
        thumb._vClickBound = true;
        thumb.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          var mediaId  = thumb.getAttribute('data-id');
          var slides   = Array.from(mainSlider.querySelectorAll('[data-product-single-media-wrapper]'));
          var idx      = slides.findIndex(function (s) { return s.getAttribute('data-id') == mediaId; });
          var curFlkty = F.data(mainSlider);
          if (idx >= 0 && curFlkty) curFlkty.select(idx);
        }, true); // capture phase fires before product-info.js bubble listeners
      });

      // Sync is-nav-selected highlight with the main slider
      function syncSelected() {
        var curFlkty = F.data(mainSlider);
        if (!curFlkty) return;
        
        var cells = curFlkty.cells;
        if (!cells || !cells.length) return;
        
        var selectedCell = cells[curFlkty.selectedIndex];
        if (!selectedCell || !selectedCell.element) return;

        var selId = selectedCell.element.getAttribute('data-id');
        
        thumbs.forEach(function (t) {
          var isMatch = (t.getAttribute('data-id') == selId);
          t.classList.toggle('is-nav-selected', isMatch);
          t.classList.toggle('active-thumb-pc', isMatch); // extra class for our forced CSS
        });
      }
      syncSelected();
      mainFlkty.on('change', syncSelected);

      return true;
    }

    function removeVertical(productInfo) {
      var thumbContainer = productInfo.querySelector('[data-product-single-media-thumbs]');
      var gallery        = productInfo.querySelector('[data-product-single-media-group]');
      if (thumbContainer) {
        thumbContainer.classList.remove('v-thumbs-ready');
        thumbContainer._vFlkty = null;
      }
      if (gallery) {
        gallery.classList.remove('gallery--v-thumbs');
        gallery.style.removeProperty('--thumb-col-height');
      }
    }

    function pollUntilReady(productInfo, attempts) {
      if (!usesDesktopThumbnailLayout()) return;
      attempts = attempts || 0;
      if (applyVertical(productInfo)) return;
      if (attempts < 40) {
        setTimeout(function () { pollUntilReady(productInfo, attempts + 1); }, 150);
      }
    }

    // Run immediately — product-info.js (loaded earlier via defer)
    // has already initialised Flickity by the time we reach this line.
    document.querySelectorAll('product-info').forEach(function (pi) {
      applyVertical(pi) || pollUntilReady(pi);
    });

    // theme:product-info:loaded fires at the START of connectedCallback(),
    // before Flickity is initialised → must poll for it.
    document.addEventListener('theme:product-info:loaded', function (e) {
      pollUntilReady(e.target);
    });

    // product-info.js re-creates Flickity nav on every theme:resize:width,
    // so we re-intercept it 600 ms later (after its 400 ms debounce).
    // On mobile: removeVertical MUST fire immediately (before product-info.js
    // re-inits Flickity ~400 ms later), otherwise Flickity is created while
    // v-thumbs-ready flex CSS is still active and the carousel breaks.
    document.addEventListener('theme:resize:width', function () {
      if (!usesDesktopThumbnailLayout()) {
        document.querySelectorAll('product-info').forEach(removeVertical);
      } else {
        setTimeout(function () {
          document.querySelectorAll('product-info').forEach(function (pi) {
            applyVertical(pi) || pollUntilReady(pi);
          });
        }, 600);
      }
    });

    // Shopify theme-editor section reload
    document.addEventListener('shopify:section:load', function () {
      setTimeout(function () {
        document.querySelectorAll('product-info').forEach(pollUntilReady);
      }, 200);
    });
  }());

  // ^^ Keep your scripts inside this IIFE function call to
  // avoid leaking your variables into the global scope.
})();
